import LoyaltyChooseDoubleRewardsOfferPill from 'takeovers/loyaltyChooseDoubleRewardsTakeover/components/LoyaltyChooseDoubleRewardsOfferPill'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import TourV2Location from './TourV2Location'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import TourV2PriceDetails from './TourV2PriceDetails'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import TourTravelTagLabel from 'components/Tours/TourTravelTagLabel'
import TourCampaignTravelTagLabel from 'components/Tours/TourCampaignTravelTagLabel'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { getRoomTypeCapacity, isTourProductTypeStyled } from 'lib/tours/tourUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import useTourStyledLabels from 'hooks/Tours/useTourStyledLabels'
import AgentHubEarnYourOwnTourLabel from 'agentHub/components/AgentHubEarnYourOwnTourLabel'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { useAppSelector } from 'hooks/reduxHooks'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import { getTourV2OperatorDisplayName } from 'components/OfferPage/TourV2/tourV2OperatorRebrand'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import GrowthCarouselCardSmall from './GrowthOfferCard/GrowthCarouselCardSmall'
import GrowthTourInclusions from './GrowthOfferCard/GrowthTourInclusions'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import useTourTags from 'hooks/TourV2/useTourTags'
import useGrowthOfferTile from './GrowthOfferCard/useGrowthOfferTile'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  onClick: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  filters?: App.OfferListFilters
}

function TourV2OfferCard(props: Props) {
  const isGrowthTile = useGrowthOfferTile()
  const intl = useIntl()
  const isRebrandEnabled = useRebrandVersion() === 'rebrand'
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()
  const { styledProductTypeTitles } = useTourStyledLabels()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
    filters,
  } = props
  const { cheapestPurchasableOption, cheapestVariation } = useCheapestOfTourV2Offer(offer)
  const tags = useTourTags(offer)
  const variationDetails = useTourV2VariationDetails(cheapestVariation)
  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const roomTypeCapacity = getRoomTypeCapacity(cheapestPurchasableOption?.roomType)
  const totalPrice = (cheapestPurchasableOption?.price ?? 0) * roomTypeCapacity
  const totalMemberPrice = (cheapestPurchasableOption?.memberPrice ?? 0) * roomTypeCapacity
  const tileBurn = useSearchOfferBurnPricing({
    price: totalPrice,
    memberPrice: totalMemberPrice,
    loyaltyProductType: getLoyaltyProductType(offer),
    viewPriceInPoints: filters?.viewPriceInPoints,
    disabled: !totalPrice,
    isLuxPlusEnabled: isLuxPlusToursEnabled,
  })

  const providerName = useMemo<string>(() => {
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    return [
      getTourV2OperatorDisplayName(offer.operatedBy, isRebrandEnabled),
      isTourProductTypeStyled(offer.productType) && offer.productType !== 'custom_tour' ? styledProductTypeTitles[offer.productType] : undefined,
    ].filter(Boolean).join(` ${sep} `)
  }, [offer, intl, isRebrandEnabled, styledProductTypeTitles])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
      const nights = variationDetails.daysCount && variationDetails.daysCount > 0 ? variationDetails.daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, variationDetails.daysCount],
  )

  if (!cheapestPurchasableOption || !cheapestVariation) {
    return null
  }

  const {
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
  } = variationDetails

  const updatedEndLocation = hasTheSameStartAndEndLocation ? startLocation : endLocation

  if (isGrowthTile) {
    const countdownTag = tags.find(tag => tag.type === 'lux_plus_general_release' && tag.end)
    const offerCountdown = offer.schedule?.end ? <OfferUrgencyCountdownLabel endDate={offer.schedule.end} tagPlace="tour-v2-urgency-tags" /> : undefined
    const imageCountdown = countdownTag?.end ? <OfferUrgencyCountdownLabel
      endDate={countdownTag.end}
      isLuxPlusEarlyAccess
      tagPlace="tour-v2-urgency-tags"
      fallback={offerCountdown}
    /> : offerCountdown
    return (
      <ProductPaletteProvider product={offer}>
        <GrowthCarouselCardSmall
          variant={variant}
          className={className}
          eagerLoadImage={eagerLoadImage}
          rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind={iconKind} iconVariant={iconVariant} />}
          image={cheapestVariation.images[0]}
          images={cheapestVariation.images}
          location={<TourV2Location startLocation={startLocation} endLocation={updatedEndLocation} variant="start_and_end" />}
          taglineInclusions={<GrowthTourInclusions variation={cheapestVariation} isLuxPlusEnabled={isLuxPlusToursEnabled} />}
          providerName={providerName}
          title={offer.name}
          priceDetails={<TourV2PriceDetails offer={offer} filters={filters} />}
          to={offerUrl}
          imageCornerLabel={imageCountdown}
          urgencyLabels={<LabelGroup>
            <AgentHubEarnYourOwnTourLabel offerId={offer.id} />
            <TourV2UrgencyTags
              offer={offer}
              showCountdown={false}
              suffixLabels={<>
                <TourTravelTagLabel tourId={offer.id} />
                <TourCampaignTravelTagLabel campaignTags={offer.campaignTags} />
              </>}
            />
          </LabelGroup>}
          onClick={onClick}
        />
      </ProductPaletteProvider>
    )
  }

  return (
    <ProductPaletteProvider product={offer}>
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        title={offer.name}
        rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
        providerName={providerName}
        image={cheapestVariation.images[0]}
        images={cheapestVariation.images}
        location={<TourV2Location startLocation={startLocation} endLocation={updatedEndLocation} variant="start_and_end" />}
        priceDetails={<TourV2PriceDetails offer={offer} variant="condensed" filters={filters} />}
        to={offerUrl}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<TourV2UrgencyTags
          offer={offer}
          suffixLabels={<>
            <TourTravelTagLabel tourId={offer.id} />
            <TourCampaignTravelTagLabel campaignTags={offer.campaignTags} />
          </>}
        />}
        onClick={onClick}
        loyaltyPointsElement={<>
          <LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
            pointsToApply={tileBurn.pointsToApplyForEarn}
            burnPriceInPoints={tileBurn.priceInPointsForEarn}
          />
          <LoyaltyChooseDoubleRewardsOfferPill offer={offer} />
        </>}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={cheapestVariation.images[0]}
        images={cheapestVariation.images}
        location={<TourV2Location startLocation={startLocation} endLocation={updatedEndLocation} variant="start_and_end" />}
        title={offer.name}
        priceDetails={<TourV2PriceDetails offer={offer} filters={filters} />}
        to={offerUrl}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<TourV2UrgencyTags
          offer={offer}
          suffixLabels={<>
            <TourTravelTagLabel tourId={offer.id} />
            <TourCampaignTravelTagLabel campaignTags={offer.campaignTags} />
          </>}
        />}
        onClick={onClick}
        loyaltyPointsElement={<>
          <LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
            pointsToApply={tileBurn.pointsToApplyForEarn}
            burnPriceInPoints={tileBurn.priceInPointsForEarn}
          />
          <LoyaltyChooseDoubleRewardsOfferPill offer={offer} />
        </>}
      />}
    </ProductPaletteProvider>
  )
}

export default TourV2OfferCard
